// src/services/ProductService.ts
import axios, { AxiosResponse } from "axios";

const API_BASE = process.env.REACT_APP_BASE_URL;

export interface ProductType {
  id: string;
  name: string;
  description?: string;
}

export interface ProductFormData {
  name: string;
  price: string;
  description: string;
  stock: string;
  categoryId: string;
  productTypeId?: string; // optional for creation
  images?: FileList | null;
}

export interface ProductImage {
  id?: number;
  signedUrl: string;
  isPrimary: boolean;
  image_key: string;
}

export interface Product {
  id: string;
  name: string;
  price: string;
  description: string;
  stock_quantity: string;
  images?: ProductImage[];
  categoryId?: string;
  category?: {
    id: string;
    name: string;
    description?: string;
  };
  product_type?: ProductType;
}

// ------------------ API response types ------------------
interface ProductApiResponse {
  data: Product[];
  pagination?: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

export const ProductService = {

  /** READ all products (normalized to array) */
  getAllProducts: async (): Promise<Product[]> => {
    const response: AxiosResponse<ProductApiResponse | Product[]> = await axios.get(
      `${API_BASE}/products`,
      { withCredentials: true }
    );

    // Normalize: array or { data: [] }
    if (Array.isArray(response.data)) return response.data;
    return response.data.data || [];
  },

  /** READ recent products */
  getRecentProducts: async (): Promise<Product[]> => {
    const res: AxiosResponse<ProductApiResponse | Product[]> = await axios.get(
      `${API_BASE}/products/recent-products`
    );

    if (Array.isArray(res.data)) return res.data;
    return res.data.data || [];
  },

  /** GET products by categoryId with optional limit */
  getProductsByCategory: async (categoryId: string, limit?: number): Promise<Product[]> => {
    const params: Record<string, string> = {};
    if (limit) params.limit = limit.toString();

    const response: AxiosResponse<{ data: Product[] }> = await axios.get(
      `${API_BASE}/products/category/${categoryId}`,
      { params, withCredentials: true }
    );

    return response.data?.data || [];
  },

  /** GET products by product type with pagination */
  getProductsByProductType: async (params: {
    productTypeId: string | number;
    page?: number;
    limit?: number;
  }): Promise<ProductApiResponse> => {
    const { productTypeId, page = 1, limit = 12 } = params;

    const res: AxiosResponse<ProductApiResponse> = await axios.get(
      `${API_BASE}/products/product-type/${productTypeId}`,
      { params: { page, limit }, withCredentials: true }
    );

    return res.data;
  },

  /** READ single product by ID */
  getProductById: async (id: string): Promise<Product> => {
    const response: AxiosResponse<Product> = await axios.get(
      `${API_BASE}/products/${id}`,
      { withCredentials: true }
    );
    return response.data;
  },

  /** FETCH products with server-side pagination & filters */
  getProducts: async ({
    page = 1,
    limit = 12,
    categoryId,
    productTypeId,
    minPrice,
    maxPrice,
    minStock,
    search,
  }: {
    page?: number;
    limit?: number;
    categoryId?: string | number;
    productTypeId?: string | number;
    minPrice?: number;
    maxPrice?: number;
    minStock?: number;
    search?: string;
  }): Promise<ProductApiResponse> => {
    const params: Record<string, string | number> = { page, limit };

    if (categoryId) params.categoryId = categoryId;
    if (productTypeId) params.productTypeId = productTypeId;
    if (minPrice !== undefined) params.minPrice = minPrice;
    if (maxPrice !== undefined) params.maxPrice = maxPrice;
    if (minStock !== undefined) params.minStock = minStock;
    if (search) params.search = search;

    const res: AxiosResponse<ProductApiResponse> = await axios.get(
      `${API_BASE}/products/filter`,
      { params, withCredentials: true }
    );

    return res.data;
  },

};
